import { hasAiDetectedLabels } from "@/store/ai-asset-review/ai-asset-review-data-selectors";
import { RootState } from "@/store/store";
import { UNTAGGED } from "@/store/tags/tags-slice";
import {
  compareDateTimes,
  GUID,
  LARGEST_MILLISECONDS,
  SMALLEST_MILLISECONDS,
} from "@faro-lotv/foundation";
import {
  AnnotationStatus,
  ExternalMarkupIElement,
  IElementMarkup,
  isIElementExternalMarkup,
  isIElementMarkup,
  isIElementMarkupAssigneeId,
  isIElementMarkupDueTime,
  isIElementMarkupState,
} from "@faro-lotv/ielement-types";
import { IElementsRecord } from "@faro-lotv/project-source";
import {
  BcfServicesIntegrationType,
  GenericUserInfo,
  GetTopicResponse,
  isAnnotationStatus,
} from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import { DateTime, Interval } from "luxon";
import { getBcfIntegrationTypeForIElementType } from "../annotations/external-annotation-utils";
import { AiAnnotationStatus } from "../filter-panel/filters/ai-status-filter";
import { UNASSIGNED_ID } from "../filter-panel/filters/assignee-filter";
import { Dates } from "../filter-panel/filters/date-filter";
import { AnnotationSortingOptions } from "../filter-panel/sort-menu-button";
import { isTagReadable } from "../tags/tag-accessibility";
import { matchAiStatusFilter } from "./annotation-panel-ai-selectors";

/** Annotation status for either a Sphere XG annotation, or a third party annotation. */
export type HybridAnnotationStatus = {
  /** The provider of the annotation, if it is an external annotation; undefined for Sphere XG annotation */
  externalAnnotationType?: BcfServicesIntegrationType;
  /** Either the Sphere XG status value, or the third party status value. */
  id: string | AnnotationStatus;
};

/**
 * String key used for ay type of status.
 * Can be generated by getKeyForHybridStatus.
 */
export type HybridAnnotationStatusKey = string;

/**
 * @returns a unique key for the status of an external annotation type
 * @param id of the status
 * @param externalAnnotationType type of external annotation
 */
export function getKeyForHybridStatus(
  id: string | AnnotationStatus,
  externalAnnotationType?: BcfServicesIntegrationType,
): HybridAnnotationStatusKey {
  return externalAnnotationType ? `${externalAnnotationType}-${id}` : id;
}

/** The options used to filter and sort the list of markups */
export type AnnotationFilteringOptions = {
  /** The list of assignees selected by the user */
  assignee: GUID[];
  /** The list of tags selected by the user */
  tags: GUID[];
  /** The list of statuses ids selected by the user */
  statusId: HybridAnnotationStatusKey[];
  /** The date range selected by the user */
  date: Dates | undefined;
  /** The sorting algorithm selected by the user */
  sorting: AnnotationSortingOptions | undefined;
  /** The text typed by the user in the search bar */
  searchText: string;
  /** The list of AI statuses selected by the user */
  aiStatus: AiAnnotationStatus[];
};

/**
 * @returns true if the input date is in the provided range, ignoring the time
 * @param date The input date to check
 * @param startDate The date representing the lower limit of the interval, if provided
 * @param endDate The date representing the upper limit of the interval, if provided
 */
function isDateInInterval(
  date: DateTime,
  startDate: DateTime | undefined = DateTime.fromMillis(SMALLEST_MILLISECONDS),
  endDate: DateTime | undefined = DateTime.fromMillis(LARGEST_MILLISECONDS),
): boolean {
  /** Luxon interval is half-open [) https://moment.github.io/luxon/api-docs/index.html#interval */
  return Interval.fromDateTimes(startDate, endDate).contains(date);
}

/** Markup with all the data necessary to filter and sort it */
export type MarkupMetadata = SphereXgMetaData | ExternalMarkupMetadata;

/** Data for the filtering and the sorting of Sphere XG markup */
export type SphereXgMetaData = {
  /** The markup iElement */
  markup: IElementMarkup;
  /** The title of the markup */
  title: string;
  /** The name of the assignee, if provided */
  assigneeName?: string;
  /** The status id (not status name) of the annotation, if provided */
  statusId?: string | null;
  /** The due date of the annotation, if provided */
  dueDate?: string;
};

/** Data for the filtering and the sorting of external markups */
export type ExternalMarkupMetadata = Omit<SphereXgMetaData, "markup"> & {
  /** The external markup iElement */
  markup: ExternalMarkupIElement;
  /** The external markup data */
  fetchedData: GetTopicResponse;
};

/**
 * Filters an external markup element based on various criteria such as workspace integrations,
 * project integrations, text search, assignee, status, and due date.
 *
 * @param externalMarkupMetadata - The external markup element to be filtered.
 * @param options - The filtering options containing search text, assignee, status, and date filters.
 * @returns The metadata of the markup element if it matches the filtering criteria, or `undefined` otherwise.
 */
export function filterExternalMarkup(
  externalMarkupMetadata: ExternalMarkupMetadata,
  options: AnnotationFilteringOptions,
): MarkupMetadata | undefined {
  if (!matchesTextSearch(options.searchText, externalMarkupMetadata.title)) {
    return;
  }

  if (noFilterSelected(options)) {
    return externalMarkupMetadata;
  }

  // Filter by assignee
  if (
    !matchAssigneeFilter(options.assignee, externalMarkupMetadata.assigneeName)
  ) {
    return;
  }
  // Filter by status
  if (
    !matchStatusFilter(
      options.statusId,
      getKeyForHybridStatus(
        externalMarkupMetadata.statusId ?? "",
        getBcfIntegrationTypeForIElementType(
          externalMarkupMetadata.markup.type,
        ),
      ),
    )
  ) {
    return;
  }
  // Filter by date
  if (!matchDateFilter(options.date, externalMarkupMetadata.dueDate)) {
    return;
  }

  return externalMarkupMetadata;
}

/**
 * Filters a given markup based on various annotation filtering options.
 *
 * @param markup - The markup object to be filtered.
 * @param iElements - A record of elements used to retrieve markup-related data.
 * @param users - An array of user information to resolve assignee names.
 * @param options - The filtering options to apply, including text search, tags, assignee, status, and date.
 * @returns A `SphereXgMetaData` object containing the filtered markup and its metadata
 * (assignee name, status, and due date) if it matches the filtering criteria, or `undefined` if it does not.
 */
function filterSphereXgMarkup(
  markup: IElementMarkup,
  iElements: IElementsRecord,
  users: GenericUserInfo[],
  options: AnnotationFilteringOptions,
): SphereXgMetaData | undefined {
  if (!matchesTextSearch(options.searchText, markup.name)) return;
  if (!matchAiStatusFilter(options.aiStatus, markup)) return;
  if (!matchTagFilter(options.tags, markup)) return;

  // Allow AI markups to be processed even without children
  if (hasAiDetectedLabels(markup)) {
    return {
      markup,
      title: markup.name,
    };
  }

  // Filter out a markup that does not have any child if a filter is selected
  if (!markup.childrenIds) {
    if (noFilterSelected(options)) {
      return {
        markup,
        title: markup.name,
      };
    }

    return;
  }

  const { assigneeId, statusId, dueDate } = getMarkupFilterFields(
    markup,
    iElements,
  );
  // Filter by assignee
  if (!matchAssigneeFilter(options.assignee, assigneeId)) {
    return;
  }
  // Filter by status
  if (!matchStatusFilter(options.statusId, statusId)) {
    return;
  }
  // Filter by date
  if (!matchDateFilter(options.date, dueDate)) {
    return;
  }

  return {
    markup,
    title: markup.name,
    assigneeName: users.find((user) => user.id === assigneeId)?.name,
    statusId,
    dueDate,
  };
}

/**
 * Sorts an array of markup metadata based on the specified sorting option.
 *
 * @param markups - The array of markup metadata to be sorted.
 * @param sorting - The sorting option to determine the order of the markups.
 * @returns The sorted array of markup metadata.
 */
export function sortMarkupMetadata(
  markups: MarkupMetadata[],
  sorting: AnnotationSortingOptions | undefined,
): MarkupMetadata[] {
  if (sorting) {
    return markups.sort(SORTING_ALGORITHMS[sorting]);
  }
  return markups;
}

// eslint-disable-next-line jsdoc/require-param
/**
 * Selector to filter and sort Sphere XG markups from the application state.
 *
 * The filtering and categorization are based on the provided `AnnotationFilteringOptions` and
 * the current state of `iElements` and `users`.
 *
 * @param state - The root state of the application.
 * @param options - The filtering options used to determine which markups to include.
 * @returns Filtered and processed Sphere XG markups as `MarkupMetadata`.
 */
export const selectFilteredAndSortedSphereXgMarkups: (
  state: RootState,
  options: AnnotationFilteringOptions,
) => MarkupMetadata[] = createSelector(
  [
    (state: RootState) => state.iElements.iElements,
    (state: RootState) => state.project.users,
    (_: RootState, options: AnnotationFilteringOptions) => options,
  ],
  (
    iElements: IElementsRecord,
    users: GenericUserInfo[],
    options: AnnotationFilteringOptions,
  ): MarkupMetadata[] => {
    const markups: MarkupMetadata[] = [];
    for (const element of Object.values(iElements)) {
      if (!element) continue;
      if (isIElementMarkup(element)) {
        const markup = filterSphereXgMarkup(element, iElements, users, options);
        if (markup) {
          markups.push(markup);
        }
      }
    }
    return markups;
  },
);

/**
 * Selector function to retrieve an array of external markup elements from the Redux state.
 *
 * @param state - The root state of the Redux store.
 * @returns An array of `ExternalMarkupIElement` objects filtered from the `iElements` state.
 */
export const selectExternalMarkupElements: (
  state: RootState,
) => ExternalMarkupIElement[] = createSelector(
  [(state: RootState) => state.iElements.iElements],
  (iElements: IElementsRecord): ExternalMarkupIElement[] => {
    const externalMarkups: ExternalMarkupIElement[] = [];
    for (const element of Object.values(iElements)) {
      if (!element) continue;
      if (isIElementExternalMarkup(element)) {
        externalMarkups.push(element);
      }
    }
    return externalMarkups;
  },
);

/**
 * @returns true if the input string contains the search text
 * @param searchText The text used to filter the names
 * @param name The name of the annotation
 */
function matchesTextSearch(searchText: string, name: string): boolean {
  return (
    searchText.length === 0 ||
    name.toLowerCase().includes(searchText.toLowerCase())
  );
}

/**
 * @returns True if the user did not filter for any specific markup property
 * @param options The list of filtering options
 */
function noFilterSelected(options: AnnotationFilteringOptions): boolean {
  return (
    options.assignee.length === 0 &&
    (options.statusId.length === 0 ||
      options.statusId.includes(AnnotationStatus.Unclassified)) &&
    (!options.date || options.date === Dates.NoDate)
  );
}

type MarkupFilterFields = {
  /** The id of the assignee */
  assigneeId?: string;
  /** The status of the markup */
  statusId?: AnnotationStatus | null;
  /** The due date of the markup */
  dueDate?: string;
};

/**
 * @returns The fields used to filter the markup
 * @param element The markup for which we want to extract the fields
 * @param iElements The list of iElements in the projects
 */
function getMarkupFilterFields(
  element: IElementMarkup,
  iElements: IElementsRecord,
): MarkupFilterFields {
  const filterFields: MarkupFilterFields = {};
  for (const childId of element.childrenIds ?? []) {
    const child = iElements[childId];
    if (!child) continue;

    // Get the assignee
    if (isIElementMarkupAssigneeId(child)) {
      filterFields.assigneeId = child.values?.[0];
    }
    // Get the status
    else if (isIElementMarkupState(child)) {
      filterFields.statusId = isAnnotationStatus(child.value)
        ? child.value
        : null;
    }
    // Get the due date
    else if (isIElementMarkupDueTime(child)) {
      filterFields.dueDate = child.value;
    }
  }

  return filterFields;
}

/**
 * @returns True if the element tags matches the tags list
 * @param tags The list of tags used to filter
 * @param element The element to check
 */
function matchTagFilter(tags: GUID[], element: IElementMarkup): boolean {
  // Check if the markup contains one of the tags in the filter
  if (tags.length > 0) {
    const hasTags =
      element.labels && element.labels.filter(isTagReadable).length > 0;
    if (!tags.includes(UNTAGGED.id) && !hasTags) {
      return false;
    } else if (
      hasTags &&
      !element.labels?.find((label) => tags.includes(label.id))
    ) {
      return false;
    }
  }
  return true;
}

/**
 * @returns True if the element assignee matches the assignees list
 * @param assignee The list of assignees used to filter
 * @param assigneeId The id of the assignee of the markup
 */
function matchAssigneeFilter(
  assignee: GUID[],
  assigneeId: GUID | undefined,
): boolean {
  if (assignee.length > 0) {
    if (assigneeId && !assignee.includes(assigneeId)) {
      return false;
    } else if (!assigneeId && !assignee.includes(UNASSIGNED_ID)) {
      return false;
    }
  }
  return true;
}

/**
 * @returns True if the element status matches the statuses list
 * @param statuses The list of statuses used to filter
 * @param status The status of the markup
 */
function matchStatusFilter(
  statuses: HybridAnnotationStatusKey[],
  status: HybridAnnotationStatusKey | null | undefined,
): boolean {
  if (statuses.length > 0) {
    if (status && !statuses.includes(status)) {
      return false;
    }
    if (!status && !statuses.includes(AnnotationStatus.Unclassified)) {
      return false;
    }
  }
  return true;
}

/**
 * @returns True if the element due date matches the dates filter list
 * @param dateFilter The type of date filter
 * @param dueDateString The string representing the due date of the markup
 */
function matchDateFilter(
  dateFilter: Dates | undefined,
  dueDateString: string | null | undefined,
): boolean {
  if (dateFilter) {
    if (dueDateString) {
      // we only care about the "day" as seen in user's local time zone, not necessary in reporter's time zone
      const dueDate = DateTime.fromISO(dueDateString);
      switch (dateFilter) {
        case Dates.BeforeToday: {
          if (
            !isDateInInterval(dueDate, undefined, DateTime.now().startOf("day"))
          ) {
            return false;
          }
          break;
        }
        case Dates.Today: {
          if (
            !isDateInInterval(
              dueDate,
              DateTime.now().startOf("day"),
              DateTime.now().endOf("day"),
            )
          ) {
            return false;
          }
          break;
        }
        case Dates.Tomorrow: {
          if (
            !isDateInInterval(
              dueDate,
              DateTime.now().plus({ day: 1 }).startOf("day"),
              DateTime.now().plus({ day: 1 }).endOf("day"),
            )
          ) {
            return false;
          }
          break;
        }
        case Dates.Next7Days: {
          const inSevenDays = DateTime.now().plus({ day: 7 }).endOf("day");
          if (
            !isDateInInterval(
              dueDate,
              DateTime.now().startOf("day"),
              inSevenDays,
            )
          ) {
            return false;
          }
          break;
        }
        case Dates.Next30Days: {
          const inThirtyDays = DateTime.now().plus({ day: 30 }).endOf("day");
          if (
            !isDateInInterval(
              dueDate,
              DateTime.now().startOf("day"),
              inThirtyDays,
            )
          ) {
            return false;
          }
          break;
        }
        case Dates.Next90Days: {
          const inNinetyDays = DateTime.now().plus({ day: 90 }).endOf("day");
          if (
            !isDateInInterval(
              dueDate,
              DateTime.now().startOf("day"),
              inNinetyDays,
            )
          ) {
            return false;
          }
          break;
        }
        case Dates.NoDate: {
          return false;
        }
      }
    } else if (dateFilter !== Dates.NoDate) {
      return false;
    }
  }
  return true;
}

const SORTING_ALGORITHMS: Record<
  AnnotationSortingOptions,
  (a: MarkupMetadata, b: MarkupMetadata) => number
> = {
  [AnnotationSortingOptions.creationDateDown]: (a, b) =>
    compareDateTimes(
      new Date(a.markup.createdAt),
      new Date(b.markup.createdAt),
    ),
  [AnnotationSortingOptions.creationDateUp]: (a, b) =>
    compareDateTimes(
      new Date(b.markup.createdAt),
      new Date(a.markup.createdAt),
    ),
  [AnnotationSortingOptions.dueDateDown]: (a, b) => {
    const aDate = a.dueDate
      ? new Date(a.dueDate).getTime()
      : Number.POSITIVE_INFINITY;
    const bDate = b.dueDate
      ? new Date(b.dueDate).getTime()
      : Number.POSITIVE_INFINITY;
    return aDate - bDate;
  },
  [AnnotationSortingOptions.dueDateUp]: (a, b) => {
    const aDate = a.dueDate
      ? new Date(a.dueDate).getTime()
      : Number.POSITIVE_INFINITY;
    const bDate = b.dueDate
      ? new Date(b.dueDate).getTime()
      : Number.POSITIVE_INFINITY;
    return bDate - aDate;
  },
  [AnnotationSortingOptions.titleDown]: (a, b) =>
    a.title.localeCompare(b.title),
  [AnnotationSortingOptions.titleUp]: (a, b) => b.title.localeCompare(a.title),
  [AnnotationSortingOptions.assignee]: (a, b) => {
    const aAssignee = a.assigneeName ?? "";
    const bAssignee = b.assigneeName ?? "";
    return aAssignee.localeCompare(bAssignee);
  },
  [AnnotationSortingOptions.status]: (a, b) => {
    const aStatus =
      a.statusId && isAnnotationStatus(a.statusId)
        ? a.statusId
        : AnnotationStatus.Unclassified;
    const bStatus =
      b.statusId && isAnnotationStatus(b.statusId)
        ? b.statusId
        : AnnotationStatus.Unclassified;
    return StatusOptionsPriority[aStatus] - StatusOptionsPriority[bStatus];
  },
};

const StatusOptionsPriority: Record<AnnotationStatus, number> = {
  [AnnotationStatus.Unclassified]: 0,
  [AnnotationStatus.Open]: 1,
  [AnnotationStatus.InProgress]: 2,
  [AnnotationStatus.ToReview]: 3,
  [AnnotationStatus.Resolved]: 4,
};
